import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAddin } from '../App';
import Modal from './Modal';
import * as webhook from '../services/webhook';

function normalizePhoneNumber(phone) {
    if (!phone) return '';
    const trimmed = phone.trim();
    if (!trimmed) return '';
    
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return trimmed; // fallback
    
    // US-based: 10 digits, or 11 digits starting with 1
    if ((digits.length === 10 && (!hasPlus || trimmed.startsWith('+1'))) ||
        (digits.length === 11 && digits.startsWith('1'))) {
        const area = digits.slice(-10, -7);
        const prefix = digits.slice(-7, -4);
        const line = digits.slice(-4);
        return `(${area}) ${prefix}-${line}`;
    }
    
    // International
    if (hasPlus || digits.length > 10) {
        let ccLen = 2;
        if (digits.startsWith('7')) {
            ccLen = 1;
        } else if (digits.length >= 2) {
            const firstTwo = digits.slice(0, 2);
            const twoDigitCCs = new Set([
                '20', '27', '30', '31', '32', '33', '34', '36', '39',
                '40', '41', '43', '44', '45', '46', '47', '48', '49',
                '51', '52', '53', '54', '55', '56', '57', '58',
                '60', '61', '62', '63', '64', '65', '66',
                '81', '82', '84', '86',
                '90', '91', '92', '93', '94', '95', '98'
            ]);
            if (!twoDigitCCs.has(firstTwo) && digits.length >= 3) {
                ccLen = 3;
            }
        }
        if (digits.length < ccLen) {
            return trimmed;
        }
        const cc = digits.slice(0, ccLen);
        const rest = digits.slice(ccLen);
        if (rest.length <= 3) {
            return `+${cc} ${rest}`;
        } else if (rest.length <= 7) {
            return `+${cc} ${rest.slice(0, 3)} ${rest.slice(3)}`;
        } else {
            return `+${cc} ${rest.slice(0, 3)} ${rest.slice(3, 7)}-${rest.slice(7)}`;
        }
    }
    
    return trimmed;
}

const EMPTY_FORM = {
    type: 'Person',
    first_name: '',
    last_name: '',
    emails: [{ address: '', name: 'Work', default_email: true }],
    phones: [{ number: '', name: 'Work', default_number: true }],
    websites: [],
    addresses: [],
    company: '',
    company_id: null,
    title: '',
};

/**
 * AddContactModal — extract contact info from email via AI, edit a form,
 * handle multiple emails/phones/websites, optionally link to a company,
 * check for duplicate emails, then submit to Clio via N8N.
 */
export default function AddContactModal({ isOpen, onClose, onSuccess }) {
    const { userEmail, itemId, conversationId, linkedMatter } = useAddin();

    // ── Form state ──
    const [form, setForm] = useState(EMPTY_FORM);

    // ── Extraction ──
    const [extracting, setExtracting] = useState(false);
    const [extracted, setExtracted] = useState(false);

    // ── Duplicate check ──
    const [emailCheckStates, setEmailCheckStates] = useState({});
    const emailDebounceRefs = useRef({});

    // ── Company search ──
    const [companyExpanded, setCompanyExpanded] = useState(false);
    const [companyQuery, setCompanyQuery] = useState('');
    const [companyResults, setCompanyResults] = useState([]);
    const [companySearching, setCompanySearching] = useState(false);
    const [companySearchError, setCompanySearchError] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const companyDebounceRef = useRef(null);

    // ── Matter search ──
    const [matterExpanded, setMatterExpanded] = useState(false);
    const [matterQuery, setMatterQuery] = useState('');
    const [matterResults, setMatterResults] = useState([]);
    const [matterSearching, setMatterSearching] = useState(false);
    const [matterSearchError, setMatterSearchError] = useState(null);
    const [selectedMatter, setSelectedMatter] = useState(null);
    const [relationshipDescription, setRelationshipDescription] = useState('');
    const matterDebounceRef = useRef(null);

    // ── Submission ──
    const [submitting, setSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type: 'error'|'success'|'warn', message: string }

    useEffect(() => {
        if (isOpen) {
            setForm(EMPTY_FORM);
            setExtracted(false);
            setExtracting(false);
            setEmailCheckStates({});
            
            setMatterQuery('');
            setMatterResults([]);
            setMatterSearchError(null);
            setMatterSearching(false);
            setRelationshipDescription('');

            setCompanyQuery('');
            setCompanyResults([]);
            setCompanySearchError(null);
            setCompanySearching(false);
            setSelectedCompany(null);
            setCompanyExpanded(false);

            setFeedback(null);
            setSubmitting(false);

            if (linkedMatter) {
                setSelectedMatter(linkedMatter);
                setMatterExpanded(true);
            } else {
                setSelectedMatter(null);
                setMatterExpanded(false);
            }
        }
    }, [isOpen, linkedMatter]);

    // ── Generic Field Changes ──
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setFeedback(null);
    };

    // ── Array Field Changes ──
    const handleArrayChange = (arrayName, index, field, value) => {
        setForm(prev => {
            const newArr = [...prev[arrayName]];
            newArr[index] = { ...newArr[index], [field]: value };
            
            // if we are changing default_email / etc, enforce only 1 default
            if (field.startsWith('default_') && value === true) {
                for (let i = 0; i < newArr.length; i++) {
                    if (i !== index) newArr[i][field] = false;
                }
            }

            return { ...prev, [arrayName]: newArr };
        });
        setFeedback(null);

        // email duplicate checking logic
        if (arrayName === 'emails' && field === 'address') {
            const trimmed = value.trim();
            const key = index.toString();
            setEmailCheckStates(prev => ({ ...prev, [key]: null }));
            if (emailDebounceRefs.current[key]) clearTimeout(emailDebounceRefs.current[key]);

            if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;

            setEmailCheckStates(prev => ({ ...prev, [key]: 'checking' }));
            emailDebounceRefs.current[key] = setTimeout(async () => {
                try {
                    const res = await webhook.checkContactEmail({ userEmail, email: trimmed });
                    setEmailCheckStates(prev => ({ ...prev, [key]: res })); 
                } catch {
                    setEmailCheckStates(prev => ({ ...prev, [key]: null }));
                }
            }, 500);
        }
    };

    const addArrayItem = (arrayName, defaultItem) => {
        setForm(prev => ({ ...prev, [arrayName]: [...prev[arrayName], defaultItem] }));
    };

    const removeArrayItem = (arrayName, index) => {
        setForm(prev => {
            const newArr = prev[arrayName].filter((_, i) => i !== index);
            return { ...prev, [arrayName]: newArr };
        });
        if (arrayName === 'emails') {
            setEmailCheckStates(prev => {
                const next = { ...prev };
                delete next[index.toString()];
                return next;
            });
        }
    };


    // ── Extract from email ──
    const handleExtract = useCallback(async () => {
        setExtracting(true);
        setFeedback(null);
        try {
            const res = await webhook.extractContactInfo({ userEmail, outlookItemId: itemId, conversationId });
            const actualRes = Array.isArray(res) ? res[0] : res;
            const c = actualRes?.contact || actualRes || {};
            setForm(prev => {
                const next = {
                    ...prev,
                    type: c.type || prev.type,
                    first_name: c.first_name || prev.first_name,
                    last_name: c.last_name || prev.last_name,
                    company: c.company || prev.company,
                    title: c.title || prev.title,
                };

                if (c.emails && c.emails.length > 0) {
                    next.emails = c.emails.map((e, i) => ({ ...e, default_email: i === 0 }));
                } else if (c.email) {
                    next.emails = [{ address: c.email, name: 'Work', default_email: true }];
                }

                if (c.phones && c.phones.length > 0) {
                    next.phones = c.phones.map((p, i) => ({ ...p, number: normalizePhoneNumber(p.number), default_number: i === 0 }));
                } else if (c.phone) {
                    next.phones = [{ number: normalizePhoneNumber(c.phone), name: 'Work', default_number: true }];
                }

                if (c.websites && c.websites.length > 0) {
                    next.websites = c.websites.map((w, i) => ({ ...w, default_web_site: i === 0 }));
                }

                if (c.addresses && c.addresses.length > 0) {
                    next.addresses = c.addresses.map((a, i) => ({
                        ...a,
                        street: a.street || '',
                        city: a.city || '',
                        province: a.province || '',
                        postal_code: a.postal_code || '',
                        country: a.country || '',
                        default_address: i === 0
                    }));
                }

                return next;
            });
            
            if (c.company) {
                setCompanyQuery(c.company);
            }

            // Trigger duplicate check for extracted emails
            const checkEmails = c.emails?.length > 0 ? c.emails : (c.email ? [{ address: c.email }] : []);
            checkEmails.forEach((emailObj, index) => {
                const addr = emailObj.address?.trim();
                if (!addr) return;
                const idxStr = index.toString();
                setEmailCheckStates(prev => ({ ...prev, [idxStr]: 'checking' }));
                if (emailDebounceRefs.current[idxStr]) clearTimeout(emailDebounceRefs.current[idxStr]);
                emailDebounceRefs.current[idxStr] = setTimeout(async () => {
                    try {
                        const dupRes = await webhook.checkContactEmail({ userEmail, email: addr });
                        setEmailCheckStates(prev => ({ ...prev, [idxStr]: dupRes }));
                    } catch {
                        setEmailCheckStates(prev => ({ ...prev, [idxStr]: null }));
                    }
                }, 200);
            });

            setExtracted(true);
        } catch (err) {
            setFeedback({ type: 'error', message: err.message || 'Failed to extract contact info from email' });
        } finally {
            setExtracting(false);
        }
    }, [userEmail, itemId, conversationId]);

    // ── Company search ──
    const handleCompanyQueryChange = useCallback((e) => {
        const q = e.target.value;
        setCompanyQuery(q);
        setForm(prev => ({ ...prev, company: q, company_id: null })); // sync input with text
        setCompanySearchError(null);

        if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);

        if (q.trim().length < 2) {
            setCompanyResults([]);
            setCompanySearching(false);
            return;
        }

        setCompanySearching(true);
        companyDebounceRef.current = setTimeout(async () => {
            try {
                const res = await webhook.searchCompanies({ userEmail, query: q.trim() });
                setCompanyResults(res?.companies || []);
            } catch (err) {
                setCompanySearchError(err.message || 'Search failed');
                setCompanyResults([]);
            } finally {
                setCompanySearching(false);
            }
        }, 350);
    }, [userEmail]);

    const handleSelectCompany = (company) => {
        setSelectedCompany(company);
        setCompanyQuery(company.name);
        setForm(prev => ({ ...prev, company: company.name, company_id: company.id }));
        setCompanyResults([]);
    };

    const handleClearCompany = () => {
        setSelectedCompany(null);
        setCompanyQuery('');
        setForm(prev => ({ ...prev, company: '', company_id: null }));
        setCompanyResults([]);
    };


    // ── Matter search ──
    const handleMatterQueryChange = useCallback((e) => {
        const q = e.target.value;
        setMatterQuery(q);
        setMatterSearchError(null);

        if (matterDebounceRef.current) clearTimeout(matterDebounceRef.current);

        if (q.trim().length < 2) {
            setMatterResults([]);
            setMatterSearching(false);
            return;
        }

        setMatterSearching(true);
        matterDebounceRef.current = setTimeout(async () => {
            try {
                const res = await webhook.searchMatters({ userEmail, query: q.trim() });
                setMatterResults(res?.matters || []);
            } catch (err) {
                setMatterSearchError(err.message || 'Search failed');
                setMatterResults([]);
            } finally {
                setMatterSearching(false);
            }
        }, 350);
    }, [userEmail]);

    const handleSelectMatter = (matter) => {
        setSelectedMatter(matter);
        setMatterQuery('');
        setMatterResults([]);
    };

    const handleClearMatter = () => {
        setSelectedMatter(null);
        setRelationshipDescription('');
        setMatterQuery('');
        setMatterResults([]);
    };

    // ── Submit ──
    const handleSubmit = useCallback(async () => {
        // Basic validation
        if (!form.first_name.trim() && !form.last_name.trim() && !form.company.trim() && !form.emails.some(e => e.address.trim())) {
            setFeedback({ type: 'error', message: 'Please provide at least a name, company, or email.' });
            return;
        }

        // Duplicate email block
        const isChecking = Object.values(emailCheckStates).some(s => s === 'checking');
        if (isChecking) {
            setFeedback({ type: 'warn', message: 'Checking for duplicate emails — please wait a moment.' });
            return;
        }

        const dupeIds = Object.keys(emailCheckStates).filter(k => emailCheckStates[k]?.exists === true);
        if (dupeIds.length > 0) {
            setFeedback({
                type: 'error',
                message: `Duplicate contact found for email. Clear or change the email to continue.`,
            });
            return;
        }

        setSubmitting(true);
        setFeedback(null);

        try {
            const validEmails = form.emails.filter(e => e.address.trim());
            const validPhones = form.phones
                .filter(p => p.number.trim())
                .map(p => ({ ...p, number: normalizePhoneNumber(p.number) }));
            const validWebsites = form.websites.filter(w => w.address.trim());
            const validAddresses = form.addresses.filter(a => a.street.trim() || a.city.trim() || a.province.trim() || a.postal_code.trim());

            if (validEmails.length > 0 && !validEmails.some(e => e.default_email)) validEmails[0].default_email = true;
            if (validPhones.length > 0 && !validPhones.some(p => p.default_number)) validPhones[0].default_number = true;
            if (validWebsites.length > 0 && !validWebsites.some(w => w.default_web_site)) validWebsites[0].default_web_site = true;
            if (validAddresses.length > 0 && !validAddresses.some(a => a.default_address)) validAddresses[0].default_address = true;

            const contact = {
                type: form.type,
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                emails: validEmails,
                phones: validPhones,
                websites: validWebsites,
                addresses: validAddresses,
                company: form.company.trim(),
                company_id: form.company_id,
                title: form.title.trim(),
            };

            await webhook.addContact({
                userEmail,
                outlookItemId: itemId,
                conversationId,
                contact,
                matterId: selectedMatter?.id || selectedMatter?.matter_id || null,
                matterNumber: selectedMatter?.number || selectedMatter?.matter_number || null,
                matterDescription: selectedMatter?.name || selectedMatter?.matter_description || null,
                relationshipDescription: selectedMatter ? relationshipDescription.trim() : null,
            });

            setFeedback({ type: 'success', message: 'Contact created successfully in Clio!' });
            if (onSuccess) onSuccess();
            setTimeout(() => {
                onClose();
            }, 1600);
        } catch (err) {
            setFeedback({ type: 'error', message: err.message || 'Failed to create contact' });
        } finally {
            setSubmitting(false);
        }
    }, [form, emailCheckStates, selectedMatter, relationshipDescription, userEmail, itemId, conversationId, onClose, onSuccess]);

    // ── Rendering helpers ──
    const renderEmailBadge = (index) => {
        const state = emailCheckStates[index.toString()];
        if (!state) return null;
        if (state === 'checking') {
            return (
                <span style={styles.badge.checking}>
                    <span className="search-spinner" style={{ width: 9, height: 9 }} /> Checking…
                </span>
            );
        }
        if (state.exists === true) {
            return (
                <span style={styles.badge.duplicate} title={`Existing contact: ${state.contact?.name}`}>
                    ⚠ Duplicate
                </span>
            );
        }
        if (state.exists === false) {
            return <span style={styles.badge.unique}>✓ Unique</span>;
        }
        return null;
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add Contact">

            {/* ── Extract button ── */}
            <div style={styles.extractRow}>
                <button
                    style={{
                        ...styles.extractBtn,
                        opacity: extracting ? 0.7 : 1,
                        cursor: extracting ? 'not-allowed' : 'pointer',
                    }}
                    onClick={handleExtract}
                    disabled={extracting}
                    id="extract-contact-btn"
                >
                    {extracting ? (
                        <><span className="btn-spinner" style={{ width: 11, height: 11 }} /> Extracting…</>
                    ) : (
                        <>✨ {extracted ? 'Re-extract from Email' : 'Extract from Email'}</>
                    )}
                </button>
                {extracted && !extracting && (
                    <span style={styles.extractedBadge}>● Fields pre-populated</span>
                )}
            </div>

            {/* ── Form ── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '58vh', overflowY: 'auto', paddingRight: '4px' }}>

                {/* Type */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {['Person', 'Company'].map(t => (
                        <label key={t} style={styles.radioLabel}>
                            <input
                                type="radio"
                                name="type"
                                value={t}
                                checked={form.type === t}
                                onChange={handleChange}
                                style={{ accentColor: 'var(--teal)' }}
                            />
                            {t}
                        </label>
                    ))}
                </div>

                {/* Name row */}
                {form.type === 'Person' && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                            <label className="form-label">First Name</label>
                            <input className="form-input" name="first_name" value={form.first_name} onChange={handleChange} placeholder="First" autoFocus />
                        </div>
                        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                            <label className="form-label">Last Name</label>
                            <input className="form-input" name="last_name" value={form.last_name} onChange={handleChange} placeholder="Last" />
                        </div>
                    </div>
                )}

                {/* Company Link / Search */}
                <div style={styles.matterSection}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                            <label className="form-label">{form.type === 'Company' ? 'Company Name' : 'Company or Employer'}</label>
                            
                            {selectedCompany ? (
                                <div style={styles.matterChip}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '11px' }}>{selectedCompany.name}</div>
                                    </div>
                                    <button style={styles.chipClear} onClick={handleClearCompany} title="Clear comp">✕</button>
                                </div>
                            ) : (
                                <div className="matter-search-input">
                                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="6.5" cy="6.5" r="5" /><line x1="10" y1="10" x2="15" y2="15" /></svg>
                                    <input
                                        type="text"
                                        placeholder="Search or enter new company name…"
                                        value={companyQuery}
                                        onChange={handleCompanyQueryChange}
                                        onFocus={() => { if (!companyExpanded) setCompanyExpanded(true); }}
                                    />
                                    {companySearching && <span className="search-spinner" />}
                                </div>
                            )}

                            {companyExpanded && companyResults.length > 0 && !selectedCompany && (
                                <div className="matter-picker" style={{ maxHeight: '160px', overflowY: 'auto', marginTop: '4px' }}>
                                    {companyResults.map((c, i) => (
                                        <div key={c.id || i} className="matter-picker-option" onClick={() => handleSelectCompany(c)}>
                                            <div className="picker-radio"><div className="picker-radio-dot" /></div>
                                            <div className="picker-info">
                                                <div className="picker-name">{c.name}</div>
                                                <div className="picker-meta">{c.email || c.phone || 'Company'}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                        </div>
                        {form.type === 'Person' && (
                            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                                <label className="form-label">Job Title</label>
                                <input className="form-input" name="title" value={form.title} onChange={handleChange} placeholder="e.g. CEO" />
                            </div>
                        )}
                    </div>
                </div>


                {/* Emails Array */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Email Addresses</span>
                        <button style={styles.addButton} onClick={() => addArrayItem('emails', { address: '', name: 'Work', default_email: false })}>+ Add</button>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {form.emails.map((email, i) => (
                            <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <select 
                                    className="form-input" 
                                    style={{ width: '80px', padding: '4px' }}
                                    value={email.name}
                                    onChange={(e) => handleArrayChange('emails', i, 'name', e.target.value)}
                                >
                                    <option value="Work">Work</option>
                                    <option value="Home">Home</option>
                                    <option value="Other">Other</option>
                                </select>
                                <div style={{ flex: 1, position: 'relative' }}>
                                    <input
                                        className="form-input"
                                        type="email"
                                        value={email.address}
                                        onChange={(e) => handleArrayChange('emails', i, 'address', e.target.value)}
                                        placeholder="email@example.com"
                                        style={emailCheckStates[i.toString()]?.exists === true ? { borderColor: 'var(--orange)' } : {}}
                                    />
                                    <div style={{ position: 'absolute', right: '6px', top: '8px'}}>{renderEmailBadge(i)}</div>
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                    <input 
                                        type="radio" 
                                        name="default_email" 
                                        checked={email.default_email}
                                        onChange={() => handleArrayChange('emails', i, 'default_email', true)}
                                        style={{ accentColor: 'var(--teal)', cursor: 'pointer' }}
                                    /> Primary
                                </label>
                                {form.emails.length > 1 && (
                                    <button style={styles.removeBtn} onClick={() => removeArrayItem('emails', i)}>✕</button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Phones Array */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Phone Numbers</span>
                        <button style={styles.addButton} onClick={() => addArrayItem('phones', { number: '', name: 'Work', default_number: false })}>+ Add</button>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {form.phones.map((phone, i) => (
                            <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <select 
                                    className="form-input" 
                                    style={{ width: '80px', padding: '4px' }}
                                    value={phone.name}
                                    onChange={(e) => handleArrayChange('phones', i, 'name', e.target.value)}
                                >
                                    <option value="Work">Work</option>
                                    <option value="Mobile">Mobile</option>
                                    <option value="Home">Home</option>
                                    <option value="Fax">Fax</option>
                                    <option value="Other">Other</option>
                                </select>
                                <input
                                    className="form-input"
                                    style={{ flex: 1 }}
                                    value={phone.number}
                                    onChange={(e) => handleArrayChange('phones', i, 'number', e.target.value)}
                                    onBlur={(e) => handleArrayChange('phones', i, 'number', normalizePhoneNumber(e.target.value))}
                                    placeholder="+1 555 000 0000"
                                />
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                    <input 
                                        type="radio" 
                                        name="default_number" 
                                        checked={phone.default_number}
                                        onChange={() => handleArrayChange('phones', i, 'default_number', true)}
                                        style={{ accentColor: 'var(--teal)', cursor: 'pointer' }}
                                    /> Primary
                                </label>
                                {form.phones.length > 1 && (
                                    <button style={styles.removeBtn} onClick={() => removeArrayItem('phones', i)}>✕</button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Websites Array */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Websites</span>
                        <button style={styles.addButton} onClick={() => addArrayItem('websites', { address: '', name: 'Work', default_web_site: false })}>+ Add</button>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {form.websites.map((web, i) => (
                            <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <select 
                                    className="form-input" 
                                    style={{ width: '80px', padding: '4px' }}
                                    value={web.name}
                                    onChange={(e) => handleArrayChange('websites', i, 'name', e.target.value)}
                                >
                                    <option value="Work">Work</option>
                                    <option value="Other">Other</option>
                                </select>
                                <input
                                    className="form-input"
                                    style={{ flex: 1 }}
                                    value={web.address}
                                    onChange={(e) => handleArrayChange('websites', i, 'address', e.target.value)}
                                    placeholder="https://example.com"
                                />
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                    <input 
                                        type="radio" 
                                        name="default_web_site" 
                                        checked={web.default_web_site}
                                        onChange={() => handleArrayChange('websites', i, 'default_web_site', true)}
                                        style={{ accentColor: 'var(--teal)', cursor: 'pointer' }}
                                    /> Primary
                                </label>
                                <button style={styles.removeBtn} onClick={() => removeArrayItem('websites', i)}>✕</button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Addresses Array */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Addresses</span>
                        <button style={styles.addButton} onClick={() => addArrayItem('addresses', { street: '', city: '', province: '', postal_code: '', country: '', name: 'Work', default_address: false })}>+ Add</button>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {form.addresses.map((addr, i) => (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <select 
                                        className="form-input" 
                                        style={{ width: '80px', padding: '4px' }}
                                        value={addr.name}
                                        onChange={(e) => handleArrayChange('addresses', i, 'name', e.target.value)}
                                    >
                                        <option value="Work">Work</option>
                                        <option value="Home">Home</option>
                                        <option value="Billing">Billing</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    <input
                                        className="form-input"
                                        style={{ flex: 1 }}
                                        value={addr.street}
                                        onChange={(e) => handleArrayChange('addresses', i, 'street', e.target.value)}
                                        placeholder="Street Address"
                                    />
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                        <input 
                                            type="radio" 
                                            name="default_address" 
                                            checked={addr.default_address}
                                            onChange={() => handleArrayChange('addresses', i, 'default_address', true)}
                                            style={{ accentColor: 'var(--teal)', cursor: 'pointer' }}
                                        /> Primary
                                    </label>
                                    <button style={styles.removeBtn} onClick={() => removeArrayItem('addresses', i)}>✕</button>
                                </div>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <input
                                        className="form-input"
                                        style={{ flex: 2 }}
                                        value={addr.city}
                                        onChange={(e) => handleArrayChange('addresses', i, 'city', e.target.value)}
                                        placeholder="City"
                                    />
                                    <input
                                        className="form-input"
                                        style={{ flex: 1 }}
                                        value={addr.province}
                                        onChange={(e) => handleArrayChange('addresses', i, 'province', e.target.value)}
                                        placeholder="State/Prov"
                                    />
                                    <input
                                        className="form-input"
                                        style={{ flex: 1 }}
                                        value={addr.postal_code}
                                        onChange={(e) => handleArrayChange('addresses', i, 'postal_code', e.target.value)}
                                        placeholder="Zip/Postal"
                                    />
                                </div>
                                <input
                                    className="form-input"
                                    value={addr.country}
                                    onChange={(e) => handleArrayChange('addresses', i, 'country', e.target.value)}
                                    placeholder="Country"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Matter link section ── */}
                <div style={styles.matterSection}>
                    <button
                        style={styles.matterToggle}
                        onClick={() => setMatterExpanded(v => !v)}
                        id="toggle-matter-section-btn"
                    >
                        <span style={{
                            display: 'inline-block',
                            transition: 'transform 0.18s',
                            transform: matterExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            marginRight: '5px',
                            fontSize: '10px',
                        }}>▶</span>
                        Link to Matter
                        <span style={{ marginLeft: '6px', fontSize: '10px', color: 'var(--grey02)', fontWeight: 400 }}>(optional)</span>
                    </button>

                    {matterExpanded && (
                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

                            {/* Selected matter chip */}
                            {selectedMatter ? (
                                <div style={styles.matterChip}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '11px' }}>
                                            {selectedMatter.name || selectedMatter.matter_description}
                                        </div>
                                        <div style={{ fontSize: '10px', color: 'var(--grey02)' }}>
                                            {selectedMatter.number || selectedMatter.matter_number}
                                            {selectedMatter.client_name && ` · ${selectedMatter.client_name}`}
                                        </div>
                                    </div>
                                    <button style={styles.chipClear} onClick={handleClearMatter} title="Clear matter">✕</button>
                                </div>
                            ) : (
                                /* Matter search */
                                <div className="matter-search-input">
                                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="6.5" cy="6.5" r="5" />
                                        <line x1="10" y1="10" x2="15" y2="15" />
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Search by name, number, or client…"
                                        value={matterQuery}
                                        onChange={handleMatterQueryChange}
                                        id="matter-search-input"
                                    />
                                    {matterSearching && <span className="search-spinner" />}
                                </div>
                            )}

                            {matterSearchError && (
                                <div className="search-error">{matterSearchError}</div>
                            )}

                            {matterResults.length > 0 && !selectedMatter && (
                                <div className="matter-picker" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                                    {matterResults.map((m, i) => (
                                        <div
                                            key={m.matter_id || m.id || i}
                                            className="matter-picker-option"
                                            onClick={() => handleSelectMatter(m)}
                                        >
                                            <div className="picker-radio"><div className="picker-radio-dot" /></div>
                                            <div className="picker-info">
                                                <div className="picker-name">{m.name || m.matter_description}</div>
                                                <div className="picker-meta">
                                                    {m.number || m.matter_number}
                                                    {m.client_name && ` · ${m.client_name}`}
                                                    {' · '}
                                                    <span className={`badge ${(m.status || 'open').toLowerCase()}`}>
                                                        {m.status || 'Open'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!matterSearching && matterQuery.trim().length >= 2 && matterResults.length === 0 && !matterSearchError && !selectedMatter && (
                                <div className="search-empty">No matters found for "{matterQuery}"</div>
                            )}

                            {/* Relationship description — only shown when matter is selected */}
                            {selectedMatter && (
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label className="form-label">Relationship Description</label>
                                    <input
                                        className="form-input"
                                        value={relationshipDescription}
                                        onChange={e => setRelationshipDescription(e.target.value)}
                                        placeholder="e.g. Opposing Counsel, Witness, Expert..."
                                        id="relationship-description-input"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Feedback ── */}
            {feedback && (
                <div
                    className={`modal-feedback ${feedback.type === 'warn' ? 'error' : feedback.type}`}
                    style={{
                        marginTop: '10px',
                        background: feedback.type === 'warn' ? 'rgba(249,172,96,0.12)' : undefined,
                        borderColor: feedback.type === 'warn' ? 'var(--orange)' : undefined,
                        color: feedback.type === 'warn' ? 'var(--orange)' : undefined,
                    }}
                >
                    {feedback.type === 'success' ? '✓' : '⚠'} {feedback.message}
                    {feedback.type === 'error' && (
                        <button style={styles.dismissBtn} onClick={() => setFeedback(null)}>Dismiss</button>
                    )}
                </div>
            )}

            {/* ── Submit ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
                <button
                    onClick={onClose}
                    disabled={submitting}
                    style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-color)', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer' }}
                >
                    Cancel
                </button>
                <button
                    className="modal-submit teal"
                    onClick={handleSubmit}
                    disabled={submitting || Object.values(emailCheckStates).some(s => s === 'checking' || s?.exists === true)}
                    style={{ width: 'auto', padding: '6px 20px', margin: 0 }}
                    id="submit-contact-btn"
                >
                    {submitting && <div className="btn-spinner" />}
                    {submitting ? 'Creating…' : 'Create Contact'}
                </button>
            </div>
        </Modal>
    );
}

// ── Local styles ──
const styles = {
    extractRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '12px',
        paddingBottom: '12px',
        borderBottom: '1px solid var(--border-color)',
    },
    extractBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        background: 'linear-gradient(135deg, var(--teal), var(--navy))',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        padding: '6px 13px',
        fontSize: '12px',
        fontWeight: 600,
        fontFamily: 'inherit',
        letterSpacing: '0.01em',
    },
    extractedBadge: {
        fontSize: '10px',
        color: 'var(--teal)',
        fontWeight: 600,
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    matterSection: {
        background: 'var(--selection-bg, rgba(80,174,179,0.06))',
        border: '1px solid var(--border-color)',
        borderRadius: '6px',
        padding: '10px',
    },
    matterToggle: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '12px',
        fontWeight: 700,
        color: 'var(--navy)',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
    },
    matterChip: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'var(--teal)',
        color: '#fff',
        borderRadius: '5px',
        padding: '7px 10px',
    },
    chipClear: {
        background: 'none',
        border: 'none',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '13px',
        lineHeight: 1,
        padding: '0 2px',
        opacity: 0.8,
    },
    badge: {
        checking: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '10px',
            color: 'var(--grey02)',
            fontWeight: 500,
        },
        duplicate: {
            fontSize: '10px',
            color: 'var(--orange)',
            fontWeight: 700,
            background: 'rgba(249,172,96,0.12)',
            padding: '1px 6px',
            borderRadius: '4px',
        },
        unique: {
            fontSize: '10px',
            color: 'var(--teal)',
            fontWeight: 700,
        },
    },
    dismissBtn: {
        marginLeft: '10px',
        fontSize: '10px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        textDecoration: 'underline',
        color: 'inherit',
        fontFamily: 'inherit',
    },
    addButton: {
        background: 'none',
        border: 'none',
        color: 'var(--teal)',
        cursor: 'pointer',
        fontSize: '11px',
        fontWeight: 600,
    },
    removeBtn: {
        background: 'none',
        border: 'none',
        color: 'var(--grey02)',
        cursor: 'pointer',
        fontSize: '12px',
        padding: '0 4px',
    }
};
